import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { GET_PUBLIC_CAMPAIGNS } from '../../graphql/queries';
import { useAuthStore } from '../../store/authStore';
import GlobalNavBar from '../layout/GlobalNavBar';
import {
  getDaysRemaining,
  isRecruitmentOpen,
} from '../../utils/campaignUtils';

// Where to send the "인플루언서로 등록하기" CTA. PublicRoute redirects
// authenticated users away from /register back to their last visited
// path, which would silently bounce a logged-in influencer back to this
// page. Send them straight to their profile instead so the button always
// goes somewhere useful.
const useSignupTarget = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? '/my-profile' : '/register?type=influencer';
};

/**
 * Influencer-side bridge / landing page.
 *
 * Layout follows `matchgo_bridge_page.html` (dark purple hero + alternating
 * neutral sections + bottom CTA), styled with Tailwind so it inherits the
 * MatchGo main-page concept rather than duplicating a competing palette.
 *
 * Mounted at `/influencer` so an external influencer landing on the link
 * sees a focused conversion page first; the main `/` route stays the
 * shared brand+influencer dashboard.
 */

interface CampaignImage {
  fileKey?: string;
  fileUrl?: string;
}
interface RecruitmentPeriod {
  startDate?: string;
  endDate?: string;
  alwaysRecruiting?: boolean;
}
interface PublicCampaign {
  campaignId: string;
  campaignName?: string;
  brandName?: string;
  campaignCategory?: string;
  representativeImages?: CampaignImage[];
  contentChannels?: string[];
  contentChannel?: string;
  rewardAmount?: number;
  recruitmentPeriod?: RecruitmentPeriod;
  numberOfRecruits?: number;
  providedValueDescription?: string;
  visitCity?: string;
  visitCountry?: string;
}

const HERO_BG = 'bg-[#26215C]'; // --purple-900 from the reference design
const ACCENT_BG = 'bg-[#534AB7]'; // --purple-600
const ACCENT_LIGHT = 'text-[#AFA9EC]'; // --purple-200
const SECTION_NEUTRAL = 'bg-[#F7F6F3]'; // --gray-50

/* ─── Status badge derived from recruitment period ─────────────────── */
const recruitBadge = (
  period: RecruitmentPeriod | undefined,
  t: (k: string) => string,
): { label: string; className: string } => {
  if (period?.alwaysRecruiting || isRecruitmentOpen(period)) {
    const days = getDaysRemaining(period?.endDate);
    if (days !== null && days <= 3) {
      return {
        label: t('mainPage.influencerBridge.badgeClosingSoon'),
        className: 'bg-[#FCEBEB] text-[#A32D2D]',
      };
    }
    return {
      label: t('mainPage.influencerBridge.badgeRecruiting'),
      className: 'bg-[#EAF3DE] text-[#3B6D11]',
    };
  }
  // Pre-launch / scheduled
  if (period?.startDate && new Date(period.startDate) > new Date()) {
    return {
      label: t('mainPage.influencerBridge.badgeUpcoming'),
      className: 'bg-[#FAEEDA] text-[#854F0B]',
    };
  }
  return {
    label: t('mainPage.influencerBridge.badgeClosed'),
    className: 'bg-[#EDECE8] text-[#4A4A47]',
  };
};

/* ─── Region label: visitCity → fallback to brandName city → category ─ */
const regionFor = (c: PublicCampaign) =>
  c.visitCity || c.visitCountry || c.campaignCategory || '';

/* ─── Hero ──────────────────────────────────────────────────────────── */
const Hero: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const signupTarget = useSignupTarget();
  return (
    <section className={`${HERO_BG} relative overflow-hidden px-5 py-14 md:px-8 md:py-20`}>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#7F77DD]/20"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-[#CECBF6]/10"
      />
      <div className="relative mx-auto max-w-3xl text-center md:text-left">
        <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-[#CECBF6]/15 px-3 py-1.5 text-[11px] font-medium tracking-wide text-[#CECBF6]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#7F77DD]" />
          {t('mainPage.influencerBridge.heroEyebrow')}
        </span>
        <h1 className="mb-3 text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
          {t('mainPage.influencerBridge.heroTitlePrefix')}{' '}
          <span className={ACCENT_LIGHT}>{t('mainPage.influencerBridge.heroTitleAccent')}</span>{' '}
          {t('mainPage.influencerBridge.heroTitleSuffix')}
        </h1>
        <p className="mb-8 text-sm leading-relaxed text-[#CECBF6]/80 md:text-base">
          {t('mainPage.influencerBridge.heroSub')}
        </p>
        <div className="mb-5 inline-flex items-center gap-2 text-xs text-[#CECBF6]">
          <span className="grid h-4 w-4 place-items-center rounded-full bg-[#CECBF6]/20 text-[9px]">
            ✓
          </span>
          {t('mainPage.influencerBridge.heroNotice')}
        </div>
        <div className="flex flex-col gap-2 md:max-w-sm">
          <button
            type="button"
            onClick={() => navigate('/discovery')}
            className={`${ACCENT_BG} rounded-xl px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#3C3489]`}
          >
            {t('mainPage.influencerBridge.heroCtaPrimary')}
          </button>
          <Link
            to={signupTarget}
            className="rounded-xl border border-[#AFA9EC]/40 px-4 py-3 text-center text-sm font-medium text-[#CECBF6] transition-colors hover:bg-white/5"
          >
            {t('mainPage.influencerBridge.heroCtaSecondary')}
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ─── Featured campaigns (real data from API) ───────────────────────── */
const FeaturedCampaigns: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<PublicCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const res = await API.graphql({
          query: GET_PUBLIC_CAMPAIGNS,
          authMode: 'AWS_IAM',
        });
        const list = (res as any)?.data?.getPublicCampaigns?.campaigns || [];
        // Show top 3, prioritising open/recruiting
        list.sort((a: PublicCampaign, b: PublicCampaign) => {
          const ao = isRecruitmentOpen(a.recruitmentPeriod) ? 0 : 1;
          const bo = isRecruitmentOpen(b.recruitmentPeriod) ? 0 : 1;
          return ao - bo;
        });
        setCampaigns(list.slice(0, 3));
      } catch (err) {
        console.error('Failed to fetch campaigns:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="border-b border-[#EDECE8] bg-white px-5 py-12 md:px-8">
      <div className="mx-auto max-w-3xl">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#534AB7]">
          {t('mainPage.influencerBridge.campaignsEyebrow')}
        </p>
        <h2 className="mb-1 text-xl font-semibold tracking-tight text-[#1A1A18] md:text-2xl">
          {t('mainPage.influencerBridge.campaignsTitle')}
        </h2>
        <p className="mb-6 text-sm leading-relaxed text-[#888780]">
          {t('mainPage.influencerBridge.campaignsSub')}
        </p>

        <ul className="flex flex-col divide-y divide-[#EDECE8] overflow-hidden rounded-2xl border border-[#EDECE8] bg-white">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 px-3 py-3">
                  <div className="h-14 w-14 flex-shrink-0 animate-pulse rounded-lg bg-[#EDECE8]" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-3/4 animate-pulse rounded bg-[#EDECE8]" />
                    <div className="h-2.5 w-1/2 animate-pulse rounded bg-[#EDECE8]" />
                  </div>
                </li>
              ))
            : campaigns.map((c) => {
                const badge = recruitBadge(c.recruitmentPeriod, t);
                const img = c.representativeImages?.[0]?.fileUrl;
                return (
                  <li key={c.campaignId}>
                    <button
                      type="button"
                      onClick={() => navigate(`/campaigns/${c.campaignId}`)}
                      className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-[#F7F6F3]"
                    >
                      {/* Thumbnail */}
                      <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-[#EEEDFE]">
                        {img ? (
                          <img
                            src={img}
                            alt={c.campaignName || ''}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-lg text-[#AFA9EC]">
                            ✺
                          </div>
                        )}
                      </div>

                      {/* Title + meta */}
                      <div className="min-w-0 flex-1">
                        <div className="mb-0.5 flex items-center gap-1.5">
                          <span
                            className={`inline-block flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold tracking-wide ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                          {regionFor(c) && (
                            <span className="truncate text-[10px] text-[#888780]">
                              {regionFor(c)}
                            </span>
                          )}
                        </div>
                        <h3 className="truncate text-[13px] font-semibold tracking-tight text-[#1A1A18]">
                          {c.campaignName || c.brandName || '-'}
                        </h3>
                        {(c.providedValueDescription || c.brandName) && (
                          <p className="mt-0.5 truncate text-[11px] text-[#888780]">
                            {c.providedValueDescription || c.brandName}
                          </p>
                        )}
                      </div>

                      {/* Action */}
                      <span
                        className="flex-shrink-0 rounded-full bg-[#534AB7] px-3 py-1.5 text-[11px] font-semibold text-white"
                        aria-hidden
                      >
                        {t('mainPage.influencerBridge.cardCtaApply')}
                      </span>
                    </button>
                  </li>
                );
              })}
        </ul>
      </div>
    </section>
  );
};

/* ─── How it works ─────────────────────────────────────────────────── */
const HowItWorks: React.FC = () => {
  const { t } = useTranslation('common');
  const steps = [
    'mainPage.influencerBridge.step1',
    'mainPage.influencerBridge.step2',
    'mainPage.influencerBridge.step3',
    'mainPage.influencerBridge.step4',
    'mainPage.influencerBridge.step5',
    'mainPage.influencerBridge.step6',
  ];
  return (
    <section className={`${SECTION_NEUTRAL} border-b border-[#EDECE8] px-5 py-12 md:px-8`}>
      <div className="mx-auto max-w-3xl">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#534AB7]">
          {t('mainPage.influencerBridge.howItWorksEyebrow')}
        </p>
        <h2 className="mb-1 text-xl font-semibold leading-snug tracking-tight text-[#1A1A18] md:text-2xl">
          {t('mainPage.influencerBridge.howItWorksTitle')}
        </h2>
        <p className="mb-6 text-sm leading-relaxed text-[#888780]">
          {t('mainPage.influencerBridge.howItWorksSub')}
        </p>
        <ol className="space-y-0">
          {steps.map((stepKey, idx) => (
            <li key={stepKey} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-[#EEEDFE] text-xs font-semibold text-[#534AB7]">
                  {idx + 1}
                </span>
                {idx < steps.length - 1 && (
                  <span className="my-1 w-px flex-1 bg-[#CECBF6]" aria-hidden />
                )}
              </div>
              <div className="flex-1 pb-5">
                <p className="mb-0.5 text-[13px] font-semibold tracking-tight text-[#1A1A18]">
                  {t(`${stepKey}.name`)}
                </p>
                <p className="text-xs leading-relaxed text-[#888780]">
                  {t(`${stepKey}.desc`)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ─── Why MatchGo ──────────────────────────────────────────────────── */
const WhyMatchgo: React.FC = () => {
  const { t } = useTranslation('common');
  const cards: Array<{ key: string; icon: React.ReactNode }> = [
    {
      key: 'why1',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="7.5" cy="7.5" r="5" stroke="#534AB7" strokeWidth="1.5" />
          <path d="M11.5 11.5L15 15" stroke="#534AB7" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      key: 'why2',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M3 9L7 13L15 5" stroke="#534AB7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      key: 'why3',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <rect x="2" y="4" width="14" height="10" rx="2" stroke="#534AB7" strokeWidth="1.5" />
          <path d="M5 8h8M5 11h5" stroke="#534AB7" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      key: 'why4',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M9 2L11 7H16L12 10L13.5 15L9 12L4.5 15L6 10L2 7H7L9 2Z" stroke="#534AB7" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      ),
    },
  ];
  return (
    <section className="border-b border-[#EDECE8] bg-white px-5 py-12 md:px-8">
      <div className="mx-auto max-w-3xl">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#534AB7]">
          {t('mainPage.influencerBridge.whyEyebrow')}
        </p>
        <h2 className="mb-1 text-xl font-semibold tracking-tight text-[#1A1A18] md:text-2xl">
          {t('mainPage.influencerBridge.whyTitle')}
        </h2>
        <p className="mb-6 text-sm leading-relaxed text-[#888780]">
          {t('mainPage.influencerBridge.whySub')}
        </p>
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          {cards.map(({ key, icon }) => (
            <div key={key} className="rounded-xl bg-[#F7F6F3] p-3.5">
              <div className="mb-2.5 grid h-8 w-8 place-items-center rounded-lg bg-[#EEEDFE]">
                {icon}
              </div>
              <p className="mb-1 text-xs font-semibold leading-tight text-[#1A1A18]">
                {t(`mainPage.influencerBridge.${key}Title`)}
              </p>
              <p className="text-[11px] leading-relaxed text-[#888780]">
                {t(`mainPage.influencerBridge.${key}Desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Creator chips ────────────────────────────────────────────────── */
const CreatorTypes: React.FC = () => {
  const { t } = useTranslation('common');
  const chips = ['chip1', 'chip2', 'chip3', 'chip4', 'chip5', 'chip6'];
  return (
    <section className={`${SECTION_NEUTRAL} border-b border-[#EDECE8] px-5 py-12 md:px-8`}>
      <div className="mx-auto max-w-3xl">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#534AB7]">
          {t('mainPage.influencerBridge.creatorEyebrow')}
        </p>
        <h2 className="mb-5 text-xl font-semibold leading-snug tracking-tight text-[#1A1A18] md:text-2xl">
          {t('mainPage.influencerBridge.creatorTitle')}
        </h2>
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#EDECE8] bg-white px-3 py-1.5 text-xs text-[#4A4A47]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#7F77DD]" />
              {t(`mainPage.influencerBridge.${c}`)}
            </span>
          ))}
        </div>
        <div className="mt-3.5 rounded-r-md border-l-[3px] border-[#C8C7C0] bg-white px-3.5 py-3 text-[11px] leading-relaxed text-[#888780]">
          {t('mainPage.influencerBridge.creatorNotice')}
        </div>
      </div>
    </section>
  );
};

/* ─── FAQ ──────────────────────────────────────────────────────────── */
const FAQ: React.FC = () => {
  const { t } = useTranslation('common');
  const faqs = ['faq1', 'faq2', 'faq3', 'faq4', 'faq5', 'faq6'];
  return (
    <section className="border-b border-[#EDECE8] bg-white px-5 py-12 md:px-8">
      <div className="mx-auto max-w-3xl">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#534AB7]">
          {t('mainPage.influencerBridge.faqEyebrow')}
        </p>
        <h2 className="mb-5 text-xl font-semibold tracking-tight text-[#1A1A18] md:text-2xl">
          {t('mainPage.influencerBridge.faqTitle')}
        </h2>
        <div>
          {faqs.map((f, i) => (
            <div
              key={f}
              className={`py-3.5 ${i < faqs.length - 1 ? 'border-b border-[#EDECE8]' : ''}`}
            >
              <p className="mb-1 text-[13px] font-semibold tracking-tight text-[#1A1A18]">
                {t(`mainPage.influencerBridge.${f}.q`)}
              </p>
              <p className="text-xs leading-relaxed text-[#888780]">
                {t(`mainPage.influencerBridge.${f}.a`)}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[#EDECE8] bg-[#F7F6F3] p-3.5">
          <span className="mt-0.5 inline-block flex-shrink-0 rounded-full bg-[#EEEDFE] px-1.5 py-0.5 text-[10px] font-semibold text-[#534AB7]">
            BETA
          </span>
          <p className="text-[11px] leading-relaxed text-[#888780]">
            {t('mainPage.influencerBridge.betaNotice')}
          </p>
        </div>
      </div>
    </section>
  );
};

/* ─── Bottom CTA ───────────────────────────────────────────────────── */
const BottomCta: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const signupTarget = useSignupTarget();
  return (
    <section className={`${HERO_BG} px-5 py-14 md:px-8`}>
      <div className="mx-auto max-w-3xl text-center md:text-left">
        <h2 className="mb-1.5 text-2xl font-semibold leading-tight tracking-tight text-white md:text-3xl">
          {t('mainPage.influencerBridge.bottomTitlePrefix')}{' '}
          <span className={ACCENT_LIGHT}>
            {t('mainPage.influencerBridge.bottomTitleAccent')}
          </span>{' '}
          {t('mainPage.influencerBridge.bottomTitleSuffix')}
        </h2>
        <p className="mb-6 text-xs leading-relaxed text-[#CECBF6]/70 md:text-sm">
          {t('mainPage.influencerBridge.bottomSub')}
        </p>
        <div className="flex flex-col gap-2 md:max-w-sm">
          <button
            type="button"
            onClick={() => navigate('/discovery')}
            className="rounded-xl bg-white px-4 py-3.5 text-sm font-semibold text-[#3C3489] transition-colors hover:bg-[#EEEDFE]"
          >
            {t('mainPage.influencerBridge.heroCtaPrimary')}
          </button>
          <Link
            to={signupTarget}
            className="rounded-xl border border-[#AFA9EC]/35 px-4 py-3 text-center text-sm font-medium text-[#CECBF6] transition-colors hover:bg-white/5"
          >
            {t('mainPage.influencerBridge.heroCtaSecondary')}
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ─── Page ─────────────────────────────────────────────────────────── */
const InfluencerLandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <GlobalNavBar variant="dark" />
      <Hero />
      <FeaturedCampaigns />
      <HowItWorks />
      <WhyMatchgo />
      <CreatorTypes />
      <FAQ />
      <BottomCta />
    </div>
  );
};

export default InfluencerLandingPage;
